import React, { useCallback, useEffect, useRef, useState } from "react";
import TextInput from "../text-input/TextInput";
import Modal from "../modal/Modal";
import GoogleMaps from "../GoogleMaps";
import { UseTranslate } from "../../hooks/Translate";
import styles from "./TextInputWithMap.module.scss";
import { loadGoogleMaps } from "../../utils/loadGoogleMaps";

interface TextInputWithMapProps {
  id: string;
  label?: string;
  name: string;
  value?: string;
  cityBounds?: any;
  cityName?: string;
  hasError?: boolean;
  errorText?: string;
  placeholder: string;
  onChange?: (value: string, details?: any) => void;
  disabled?: boolean;
}

const TextInputWithMap: React.FC<TextInputWithMapProps> = (props) => {
  const [inputValue, setInputValue] = useState(props.value || "");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [selectedAddressTitle, setSelectedAddressTitle] = useState<string>("");
  const selectedAddressTitleRef = useRef<string>("");
  const inputValueRef = useRef<string>(inputValue);

  useEffect(() => {
    selectedAddressTitleRef.current = selectedAddressTitle;
  }, [selectedAddressTitle]);

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  const getCityCenter = () => {
    if (props.cityBounds && props.cityBounds.getCenter) {
      const center = props.cityBounds.getCenter();
      return { lat: center.lat(), lng: center.lng() };
    }
    return { lat: 41.7151, lng: 44.8271 }; //default to tbilisi
  };

  const [mapPosition, setMapPosition] = useState<any>(getCityCenter());

  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [translate] = UseTranslate();

  const convertBoundsToPlacesFormat = (googleMapsBounds: any) => {
    if (!googleMapsBounds) return null;

    if (googleMapsBounds.getNorthEast && googleMapsBounds.getSouthWest) {
      const ne = googleMapsBounds.getNorthEast();
      const sw = googleMapsBounds.getSouthWest();
      return {
        north: ne.lat(),
        south: sw.lat(),
        east: ne.lng(),
        west: sw.lng(),
      };
    }

    if (googleMapsBounds.north !== undefined) {
      return googleMapsBounds;
    }

    return null;
  };

  const sortPredictionsByCity = (predictions: any[], selectedCity: string) => {
    if (!selectedCity) return predictions;

    return predictions.sort((a, b) => {
      const aDescription = a.description.toLowerCase();
      const bDescription = b.description.toLowerCase();
      const cityLower = selectedCity.toLowerCase();

      const aInCity = aDescription.includes(cityLower);
      const bInCity = bDescription.includes(cityLower);

      if (aInCity && !bInCity) return -1;
      if (!aInCity && bInCity) return 1;

      return 0;
    });
  };

  useEffect(() => {
    loadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY!).then(() => {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      const dummyMap = new window.google.maps.Map(
        document.createElement("div"),
      );
      placesService.current = new window.google.maps.places.PlacesService(
        dummyMap,
      );
    });

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setInputValue(props.value || "");
  }, [props.value]);

  useEffect(() => {
    setSuggestions([]);
    setSelectedPlace(null);
    setSelectedAddressTitle("");
    selectedAddressTitleRef.current = "";
    setShowSuggestions(false);
  }, [props.cityName]);

  // Update map position when city changes
  useEffect(() => {
    if (props.cityBounds) {
      const cityCenter = getCityCenter();
      setMapPosition(cityCenter);
    }
  }, [props.cityBounds]);

  const isGoogleMapsLoaded = () => {
    return window.google && window.google.maps;
  };

  const fetchPredictions = useCallback(
    (value: string) => {
      if (value.length > 2 && autocompleteService.current) {
        const request: any = {
          input: value,
          componentRestrictions: { country: "ge" },
        };

        if (props.cityBounds) {
          const convertedBounds = convertBoundsToPlacesFormat(props.cityBounds);
          if (convertedBounds) {
            request.bounds = convertedBounds;
          }
        }

        autocompleteService.current.getPlacePredictions(
          request,
          (predictions: any[] | null, status: any) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              const sortedPredictions = props.cityName
                ? sortPredictionsByCity(predictions, props.cityName)
                : predictions;

              setSuggestions(sortedPredictions.slice(0, 8));
              setShowSuggestions(true);
            } else {
              setSuggestions([]);
              setShowSuggestions(false);
            }
          },
        );
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    },
    [props.cityBounds, props.cityName],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    inputValueRef.current = value;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchPredictions(value);
    }, 750);
  };

  const handleSuggestionClick = (prediction: any) => {
    if (placesService.current && prediction.place_id) {
      placesService.current.getDetails(
        { placeId: prediction.place_id },
        (place: any, status: string) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            setMapPosition(place.geometry.location);
            setInputValue(prediction.description || "");
            setSelectedPlace({ ...place, placeId: prediction.place_id });
            setShowSuggestions(false);
            //store selected address title for onblur validation
            setSelectedAddressTitle(prediction.description || "");
            selectedAddressTitleRef.current = prediction.description || "";

            // Format the place object to match backend requirements
            const formattedPlace = {
              placeId: prediction.place_id,
              ...place,
              geolocation: {
                type: "Point",
                coordinates: [
                  place.geometry.location.lat(),
                  place.geometry.location.lng(),
                ],
              },
            };

            if (props.onChange) {
              props.onChange(prediction.description || "", formattedPlace);
            }
          }
        },
      );
    }
  };

  const handleMapOpen = () => {
    setIsMapOpen(true);
  };

  const handleMapClose = () => {
    setIsMapOpen(false);
  };

  const handleMapPrediction = (prediction: any) => {
    setInputValue(prediction.title);
    inputValueRef.current = prediction.title;
    setSelectedAddressTitle(prediction.title);
    selectedAddressTitleRef.current = prediction.title;
    setSelectedPlace(prediction);
    setIsMapOpen(false);

    if (props.onChange) {
      props.onChange(prediction.title, prediction);
    }
  };

  const handleFocus = () => {
    setShowSuggestions(suggestions.length > 0);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      //ifuser leaves the field without confirming an address clear the input
      const latestSelectedTitle = selectedAddressTitleRef.current;
      const latestInput = inputValueRef.current;
      if (!latestSelectedTitle || latestInput.trim() !== latestSelectedTitle) {
        if (inputValue.trim().length > 0) {
          setInputValue("");
        }
        if (selectedPlace) {
          setSelectedPlace(null);
        }
        setSelectedAddressTitle("");
      }
    }, 200);
  };

  return (
    <div className={styles.container}>
      <TextInput
        id={props.id}
        label={props.label}
        name={props.name}
        value={inputValue}
        hasError={props.hasError}
        errorText={props.errorText}
        placeholder={props.placeholder}
        disabled={props.disabled}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        showMapButton={true}
        onMapButtonClick={handleMapOpen}
        suggestions={suggestions}
        onSuggestionClick={handleSuggestionClick}
        showSuggestions={showSuggestions}
      />

      <Modal
        visible={isMapOpen}
        onClose={handleMapClose}
        title={translate("loyalty.registrationForm.selectLocation")}
        showClose
        stretch
        style={{
          alignItems: "center",
          justifyContent: "center",
        }}
        content={
          <div className={styles.mapContainer}>
            {isGoogleMapsLoaded() ? (
              <GoogleMaps
                zoom={13}
                class={styles.map}
                geoCodeByPinLocation={true}
                autoZoom={false}
                showMarker={true}
                centerPosition={mapPosition}
                onPrediction={handleMapPrediction}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                Loading map...
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default TextInputWithMap;
